import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { GovCoError } from '@/utils';
import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  type NormalizedCacheObject,
  type Operation,
  type ServerError,
  type ServerParseError,
} from '@apollo/client';
import { type NetworkError } from '@apollo/client/errors';
import { onError, type ErrorResponse } from '@apollo/client/link/error';
import * as Sentry from '@sentry/nextjs';
import merge from 'deepmerge';
import { env } from 'env.mjs';
import { type GraphQLError } from 'graphql';
import Cookies from 'js-cookie';
import isEqual from 'lodash/isEqual';

import { TOKEN_ADMIN, TOKEN_CLIENT } from '@/utils/constants';
import { modalInfoSetter } from '@/utils/store/store';

import cache from './cache';

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

// Evita spamear el modal cuando el internet es intermitente.
let lastConnectivityModalAt = 0;
const CONNECTIVITY_MODAL_COOLDOWN_MS = 10_000;

// Verificación bajo sospecha (sin polling): cuando hay errores de red, intentamos
// comprobar si el navegador puede llegar al sitio (recurso estático en CDN).
type ReachabilityProbeState = {
  at: number;
  reachable: boolean | null;
  inFlight: Promise<boolean> | null;
};

const reachabilityProbe: ReachabilityProbeState = {
  at: 0,
  reachable: null,
  inFlight: null,
};

const REACHABILITY_PROBE_COOLDOWN_MS = 15_000;

function startReachabilityProbeIfNeeded() {
  if (typeof window === 'undefined') return;
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    reachabilityProbe.reachable = false;
    reachabilityProbe.at = Date.now();
    return;
  }

  const now = Date.now();
  if (reachabilityProbe.inFlight) return;
  if (now - reachabilityProbe.at < REACHABILITY_PROBE_COOLDOWN_MS) return;

  reachabilityProbe.inFlight = (async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      const url = `/health.txt?_ts=${Date.now()}`;

      const res = await fetch(url, { method: 'GET', cache: 'no-store', signal: controller.signal });
      clearTimeout(timeoutId);

      reachabilityProbe.reachable = res.ok;
      reachabilityProbe.at = Date.now();
      return res.ok;
    } catch {
      reachabilityProbe.reachable = false;
      reachabilityProbe.at = Date.now();
      return false;
    } finally {
      reachabilityProbe.inFlight = null;
    }
  })();
}

function getConnectivityMessageWithProbe(error: NetworkError): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'No tienes conexión a internet. Por favor, verifica tu conexión e intenta de nuevo.';
  }

  // Si el probe dice que ni siquiera podemos llegar al sitio, es muy probable que sea red.
  if (reachabilityProbe.reachable === false) {
    return 'Parece que tu conexión está inestable o no tienes acceso a internet. Intenta nuevamente cuando se estabilice.';
  }

  // Si podemos llegar al sitio pero falla GraphQL, puede ser el servicio/backend o la ruta externa.
  if (reachabilityProbe.reachable === true) {
    return 'No fue posible conectarse al servicio en este momento. Intenta nuevamente en unos segundos.';
  }

  // Sin resultado aún, fallback general.
  return getNetworkErrorMessage(error);
}

type ServerException = ServerError & {
  result: {
    errors: Array<{
      message: string;
      code?: string;
      extensions?: { code: string };
    }>;
  };
};

export type CustomGraphQLError = ReadonlyArray<GraphQLError & { status: number }>;

export enum SentrySeverity {
  Fatal = 'fatal',
  Critical = 'critical',
  Error = 'error',
  Warning = 'warning',
  Log = 'log',
  Info = 'info',
  Debug = 'debug',
}

function isServerError(error: NetworkError): error is ServerException {
  return !!error && 'result' in error;
}

function isServerParseError(error: NetworkError): error is ServerParseError {
  return !!error && 'bodyText' in error;
}

/**
 * Detecta si el error de red es causado por problemas de conectividad
 * (sin conexión a internet, timeout, etc.)
 */
function isNetworkConnectivityError(error: NetworkError): boolean {
  if (!error) return false;

  // TypeError: Failed to fetch - indica generalmente problemas de red
  if (error.message?.includes('Failed to fetch')) return true;
  if (error.message?.includes('Network request failed')) return true;
  if (error.message?.includes('NetworkError')) return true;
  if (error.message?.includes('net::ERR_')) return true;
  if (error.message?.includes('ECONNREFUSED')) return true;
  if (error.message?.includes('ETIMEDOUT')) return true;
  if (error.message?.includes('ENOTFOUND')) return true;
  if (error.name === 'TypeError' && error.message?.includes('fetch')) return true;

  // Verificar si el navegador está offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;

  return false;
}

/**
 * Obtiene un mensaje amigable para errores de red
 */
function getNetworkErrorMessage(error: NetworkError): string {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'No tienes conexión a internet. Por favor, verifica tu conexión e intenta de nuevo.';
  }

  if (error?.message?.includes('timeout') || error?.message?.includes('ETIMEDOUT')) {
    return 'La solicitud tardó demasiado tiempo. Tu conexión puede ser lenta o inestable.';
  }

  return 'Hubo un problema de conexión. Por favor, verifica tu conexión a internet e intenta de nuevo.';
}

type LinkConditionPair = {
  condition: (operation: Operation) => boolean;
  link: HttpLink;
};
function getApolloLink(pairs: LinkConditionPair[]): ApolloLink {
  if (pairs.length === 1) {
    return pairs[0].link;
  } else {
    const [firstPair, ...restPairs] = pairs;
    return ApolloLink.split(firstPair.condition, firstPair.link, getApolloLink(restPairs));
  }
}

const errorLink = onError(({ graphQLErrors, networkError, operation, response }: ErrorResponse) => {
  const errors = graphQLErrors as CustomGraphQLError;
  if (errors && errors.length > 0) {
    const statusCode = errors[0]?.status;
    const errorMessage = errors[0].message;
    const operationName = operation.operationName;
    const setter = modalInfoSetter;
    if (operationName === 'userInfoByToken') {
      Cookies.remove('token');
      Cookies.remove(TOKEN_ADMIN);
      location.href = '/admin';
    } else if (errorMessage == 'Invalid token' && statusCode == 400) {
      Cookies.remove('token');
      Cookies.remove(TOKEN_ADMIN);
      setter({
        modalVisible: true,
        message: errorMessage,
        type: 'error',
        reload: true,
      });
    } else {
      const shouldShowMessage =
        ((operationName == 'UpdateUserInformation' ||
          operationName == 'EnableAndDisableDoubleVerification' ||
          operationName == 'ApprovalJwt') &&
          statusCode == 401) ||
        (operationName == 'SignInAdmin' && statusCode == 400);
      if (
        (statusCode == 400 || statusCode == 401 || statusCode == 403 || statusCode == 404) &&
        !shouldShowMessage
      ) {
        setter({ modalVisible: true, message: errorMessage, type: 'error' });
      }
    }
  }

  if (errors) {
    errors.forEach((error) => {
      Sentry.withScope((scope) => {
        scope.setTag('graphql', 'server');
        scope.setLevel(SentrySeverity.Warning);
        scope.setExtra('operationName', operation.operationName);
        scope.setExtra('variables', operation.variables);
        scope.setExtra('response', response);

        const name: string = (error.extensions?.code as string) || 'GrahpqlError';

        const govCoError = new GovCoError(name, error.message, error.stack || new Error().stack);

        Sentry.captureException(govCoError);
      });
    });
  }

  if (networkError) {
    const setter = modalInfoSetter;

    // Manejar específicamente errores de conectividad
    if (isNetworkConnectivityError(networkError)) {
      // Iniciar una verificación ligera (bajo sospecha) para mejorar el mensaje sin hacer polling.
      startReachabilityProbeIfNeeded();

      const userFriendlyMessage = getConnectivityMessageWithProbe(networkError);

      const now = Date.now();
      if (now - lastConnectivityModalAt > CONNECTIVITY_MODAL_COOLDOWN_MS) {
        lastConnectivityModalAt = now;
        setter({
          modalVisible: true,
          message: userFriendlyMessage,
          type: 'info', // Usamos 'info' para errores de conectividad del usuario
        });
      }

      // Log a Sentry con severidad menor para errores de conectividad del usuario
      Sentry.withScope((scope) => {
        scope.setTag('error_type', 'network_connectivity');
        scope.setTag(
          'user_online',
          String(typeof navigator !== 'undefined' ? navigator.onLine : 'unknown')
        );
        scope.setTag('reachability_probe', String(reachabilityProbe.reachable));
        scope.setLevel(SentrySeverity.Info);
        scope.setExtra('operationName', operation.operationName);
        scope.setExtra('errorMessage', networkError.message);

        Sentry.captureMessage(`Network connectivity issue: ${operation.operationName}`);
      });

      return; // No procesar como error crítico
    }

    Sentry.withScope((scope) => {
      scope.setTag('graphql', 'client');
      scope.setLevel(SentrySeverity.Error);
      scope.setExtra('operationName', operation.operationName);
      scope.setExtra('variables', operation.variables);
      scope.setExtra('response', JSON.stringify(response));

      if (isServerError(networkError)) {
        networkError.result.errors?.forEach((error) => {
          const name: string = error.extensions?.code || error.code || 'GrahpqlError';
          Sentry.captureException(new GovCoError(name, error.message, networkError.stack));
        });
      }

      if (isServerParseError(networkError)) {
        Sentry.captureException(
          new GovCoError(networkError.name, networkError.bodyText, networkError.stack)
        );
      }
    });
  }
});

export const httpLink = new HttpLink({
  uri: env.NEXT_PUBLIC_GRAPHQL_URI,
});

export const httpLinkMagneto = new HttpLink({
  uri: env.NEXT_PUBLIC_MAGNETO_GRAPHQL_URI,
});

// function CreateLocaleLink(preview?: string) {
//   const router = useRouter()
//   const isAdminRoute = router.pathname.startsWith('/admin')
//   return new ApolloLink((operation, forward) => {
//     const tokenAdmin = Cookies.get(TOKEN_ADMIN)
//     const tokenClient = Cookies.get(TOKEN_CLIENT)
//     const context = operation.getContext()
//     const { headers } = context
//     const _headers: { [key: string]: string } = { ...headers }
//     if (preview) {
//       _headers['activeversion'] = preview
//     }

//     const _token = isAdminRoute ? tokenAdmin : tokenClient

//     // empanada 🥟
//     if (!headers?.mondongo) {
//       operation.setContext({
//         headers: { ..._headers, Authorization: _token ? `Bearer ${_token}` : '' },
//       })
//     } else {
//       operation.setContext({
//         headers: { ..._headers, Authorization: '' },
//       })
//     }

//     if (
//       operation.operationName.toLocaleLowerCase().includes('magneto') ||
//       operation.getContext().apiName === 'magneto'
//     )
//       return forward(operation)

//     return forward(operation)
//   })
// }

function createLocaleLink(isAdminRoute?: boolean) {
  return new ApolloLink((operation, forward) => {
    // On server-side, avoid accessing `window` or `document`-based cookies.
    const isBrowser = typeof window !== 'undefined';

    const tokenAdmin = isBrowser ? Cookies.get(TOKEN_ADMIN) : '';
    const tokenClient = isBrowser ? Cookies.get(TOKEN_CLIENT) : '';

    const context = operation.getContext();
    const { headers } = context;

    const skipAuth = Boolean((context as any)?.skipAuth);

    // Safely read preview query param only in browser
    const preview = isBrowser
      ? new URLSearchParams(window.location.search).get('preview')
      : undefined;

    // Clone headers and add activeversion if preview exists
    const _headers: Record<string, string> = {
      ...(headers || {}),
      ...(preview ? { activeversion: preview } : {}),
    };

    // Assign token based on isAdminRoute; on server tokens will be blank
    const token = isAdminRoute ? tokenAdmin : tokenClient;

    const dependencyId = isBrowser ? Cookies.get('dependencyId') || '' : '';

    const nextHeaders: Record<string, string> = {
      ..._headers,
      language: 'es',
      // Only add dependency-id if not already present
      ...(!_headers['dependency-id'] ? { 'dependency-id': dependencyId } : {}),
    };

    // Authorization handling:
    // - Never send an empty Authorization header.
    // - Allow per-operation opt-out via context.skipAuth.
    if (skipAuth) {
      delete nextHeaders.Authorization;
    } else if (!('Authorization' in nextHeaders) && token) {
      nextHeaders.Authorization = `Bearer ${token}`;
    } else if (nextHeaders.Authorization === '') {
      delete nextHeaders.Authorization;
    }

    operation.setContext({
      headers: nextHeaders,
    });

    return forward(operation);
  });
}

export function createApolloClient(isAdminRoute?: boolean) {
  const localeLink = createLocaleLink(isAdminRoute);

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: from([
      localeLink,
      errorLink,
      getApolloLink([
        {
          condition: (opt) => {
            return (
              opt.operationName.toLocaleLowerCase().includes('magneto') ||
              opt.getContext().apiName === 'magneto'
            );
          },
          link: httpLinkMagneto,
        },
        {
          condition: () => {
            return true;
          },
          link: httpLink,
        },
      ]),
    ]),
    cache,
    name: 'govco-frontend',
  });
}

// export function initializeApollo(initialState = null, preview = undefined) {
//   const _apolloClient = createApolloClient(preview)

//   if (initialState) {
//     const existingCache = _apolloClient.extract()

//     const data = merge(existingCache, initialState, {
//       arrayMerge: (destinationArray, sourceArray) => [
//         ...sourceArray,
//         ...destinationArray.filter((d) => sourceArray.every((s) => !isEqual(d, s))),
//       ],
//     })

//     _apolloClient.cache.restore(data)
//   }
//   if (typeof window === 'undefined') return _apolloClient
//   if (!apolloClient) apolloClient = _apolloClient

//   return _apolloClient
// }

export function initializeApollo(initialState = null, isAdminRoute?: boolean) {
  const _apolloClient = createApolloClient(isAdminRoute);

  // Hidrata la cache con el estado inicial
  if (initialState) {
    const existingCache = _apolloClient.extract();
    const data = merge(existingCache, initialState);
    _apolloClient.cache.restore(data);
  }

  // En SSR, devolvemos siempre uno nuevo;
  // en el cliente, reutilizamos instancia para no duplicar
  if (typeof window === 'undefined') return _apolloClient;
  if (!apolloClient) apolloClient = _apolloClient;

  return apolloClient;
}

export function addApolloState(client: ApolloClient<NormalizedCacheObject>, pageProps: any) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

export function useApollo({ isAdminRoute, ...pageProps }: any) {
  const state = pageProps[APOLLO_STATE_PROP_NAME];

  // Llamamos initializeApollo con los valores extras
  const store = useMemo(() => initializeApollo(state, isAdminRoute), [state, isAdminRoute]);

  return store;
}
